import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Video, Search, Loader2, CalendarPlus, Check, Copy, UserPlus, Users, Clock, Zap } from 'lucide-react';
import { api } from '../../lib/api';

const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13,167,202,0.15)]';

const DEFAULT_LANGS = ['en-US', 'es-US'];

// Start a video visit straight from the Video Visits tab. Two kinds of
// invitee: a Patient (one of the org's contacts — invite goes to their
// account + email/SMS) or a Visitor (anyone, by name + email/phone — they
// join through a tokenized public link, no account needed). Scheduling an
// appointment instead stays one click away.
export default function NewVisitModal({ orgId, onClose }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('patient'); // 'patient' | 'visitor'
  const [contacts, setContacts] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [visitors, setVisitors] = useState([{ name: '', email: '', phone: '' }]);
  const [title, setTitle] = useState('');
  const [langs, setLangs] = useState(DEFAULT_LANGS);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  // After a visitor visit is created: { sessionId, guests: [{name, guest_url}] }
  // — show the copy-link step before entering the room, since the links are
  // each visitor's only way in.
  const [created, setCreated] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  // 'now' starts the room immediately; 'later' books an appointment with a
  // video room attached — the patient's join link goes out right away and
  // the visit shows on the calendar AND in this tab's Today list.
  const [when, setWhen] = useState('now');
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('');
  const [schedDuration, setSchedDuration] = useState(30);

  const MAX_VISITORS = 8;
  const setVisitor = (i, field, value) =>
    setVisitors((prev) => prev.map((v, j) => (j === i ? { ...v, [field]: value } : v)));
  const addVisitor = () =>
    setVisitors((prev) => (prev.length < MAX_VISITORS ? [...prev, { name: '', email: '', phone: '' }] : prev));
  const removeVisitor = (i) =>
    setVisitors((prev) => (prev.length > 1 ? prev.filter((_, j) => j !== i) : prev));

  useEffect(() => {
    if (!orgId) return;
    api.getContacts(orgId).then(setContacts).catch((err) => setError(err.message));
    api.getTelehealthLanguages(orgId).then(setLanguages).catch(() => {});
  }, [orgId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return contacts || [];
    return (contacts || []).filter((c) =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(term)
      || c.email?.toLowerCase().includes(term)
      || c.phone?.includes(term));
  }, [contacts, search]);

  const selected = (contacts || []).find((c) => c.id === selectedId) || null;
  const visitorsReady = visitors.length > 0 && visitors.every((v) => v.name.trim().length > 0);
  const scheduling = when === 'later';
  const canStart = (mode === 'patient' ? !!selected : visitorsReady)
    && (!scheduling || (schedDate && schedTime));

  const toggleLang = (code) => setLangs((prev) =>
    prev.includes(code) ? prev.filter((l) => l !== code) : [...prev, code]);

  const handleStart = async () => {
    if (!canStart) return;
    setStarting(true); setError('');
    try {
      if (scheduling) {
        // Book the appointment first (that's what the calendar shows), then
        // attach the video room so the invite carries the join link and the
        // scheduled time. Visitor visits book a client-less appointment.
        const startMs = new Date(`${schedDate}T${schedTime}:00`).getTime();
        const end = new Date(startMs + (Number(schedDuration) || 30) * 60000);
        const pad = (n) => String(n).padStart(2, '0');
        const endsLocal = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}:00`;
        const visitorNames = visitors.map((v) => v.name.trim()).filter(Boolean);
        const appt = await api.createAppointment(orgId, {
          type: 'video_visit',
          title: title.trim() || (mode === 'patient' ? 'Video visit' : `Video visit — ${visitorNames.join(', ')}`),
          client_id: mode === 'patient' ? selected.id : null,
          starts_at: `${schedDate}T${schedTime}:00`,
          ends_at: endsLocal,
          status: 'pending',
        });
        const session = await api.createTelehealthSession(orgId, {
          appointment_id: appt.id,
          languages: langs.length ? langs : undefined,
          ...(mode === 'visitor'
            ? { guests: visitors.map((v) => ({
                name: v.name.trim(),
                email: v.email.trim() || undefined,
                phone: v.phone.trim() || undefined,
              })) }
            : {}),
        });
        setCreated({
          scheduled: {
            when: new Date(`${schedDate}T${schedTime}:00`),
            patient: mode === 'patient'
              ? `${selected.first_name} ${selected.last_name}`
              : visitorNames.join(', '),
          },
          sessionId: session.id,
          guests: session.guests || [],
        });
        setStarting(false);
        return;
      }
      const body = {
        title: title.trim() || undefined,
        languages: langs.length ? langs : undefined,
        ...(mode === 'patient'
          ? { client_id: selected.id }
          : {
              guests: visitors.map((v) => ({
                name: v.name.trim(),
                email: v.email.trim() || undefined,
                phone: v.phone.trim() || undefined,
              })),
            }),
      };
      const session = await api.createTelehealthSession(orgId, body);
      if (mode === 'visitor' && session.guests?.length) {
        // Don't jump straight into the room — the staffer may need to copy
        // links (a visitor with no email/phone gets theirs no other way).
        setCreated({ sessionId: session.id, guests: session.guests });
        setStarting(false);
        return;
      }
      navigate(`/telehealth/${session.id}`);
    } catch (err) {
      setError(err.message || 'Could not start the visit');
      setStarting(false);
    }
  };

  const copyLink = async (i) => {
    try {
      await navigator.clipboard.writeText(created.guests[i].guest_url);
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch { /* clipboard blocked — the link is visible to select manually */ }
  };

  const modeTab = (value, Icon, label) => (
    <button
      onClick={() => { setMode(value); setError(''); }}
      className={`flex-1 px-3 py-[9px] rounded-[10px] text-[12px] font-semibold border transition-all flex items-center justify-center gap-1.5 ${mode === value
        ? 'gradient-accent text-white border-transparent shadow-glow'
        : 'bg-tertiary text-secondary-c border-theme hover:bg-hover-surface'}`}
    >
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-[520px] bg-elevated rounded-[14px] border border-theme shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-theme">
          <div>
            <h2 className="text-[15px] font-bold text-primary-c flex items-center gap-2">
              <Video size={16} className="text-accent-light" /> New video visit
            </h2>
            <p className="text-[11px] text-muted-c mt-0.5">
              {created
                ? 'Visit created — share the link below.'
                : 'The invitee gets their secure join link by email and text automatically.'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-tertiary border border-theme text-secondary-c flex items-center justify-center hover:bg-hover-surface transition-all">
            <X size={15} />
          </button>
        </div>

        {created && created.scheduled ? (
          <div className="px-6 py-6 text-center">
            <div className="w-[46px] h-[46px] rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-3">
              <Check size={20} />
            </div>
            <div className="text-[15px] font-bold text-primary-c">Video visit scheduled</div>
            <p className="text-[12.5px] text-muted-c mt-1 max-w-[360px] mx-auto">
              {created.scheduled.patient} ·{' '}
              {created.scheduled.when.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
              The join link was sent by email and text; the visit is on the calendar and will be
              in Today here, ready to join.
            </p>
            {created.guests?.length > 0 && (
              <div className="text-left space-y-2.5 max-h-[240px] overflow-y-auto mt-5">
                {created.guests.map((g, i) => (
                  <div key={g.id || i} className="rounded-[10px] border border-theme bg-tertiary p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[12px] font-semibold text-primary-c truncate">{g.name}</span>
                      <button
                        onClick={() => { navigator.clipboard?.writeText(g.guest_url).catch(() => {}); }}
                        className="shrink-0 px-2.5 py-[5px] rounded-lg text-[11px] font-semibold text-secondary-c bg-elevated border border-theme hover:bg-hover-surface transition-all flex items-center gap-1"
                      >
                        <Copy size={11} /> Copy link
                      </button>
                    </div>
                    <div className="text-[10.5px] text-primary-c font-mono break-all select-all">{g.guest_url}</div>
                    <p className="text-[10px] text-muted-c mt-1">
                      {(g.email || g.phone)
                        ? `Invite sent with the scheduled time to ${[g.email, g.phone].filter(Boolean).join(' and ')}.`
                        : 'No email or phone — this link is their only way in.'}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-center gap-2 mt-5">
              <button
                onClick={onClose}
                className="px-4 py-[9px] rounded-lg text-[12px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
              >
                Done
              </button>
              <button
                onClick={() => navigate(`/telehealth/${created.sessionId}`)}
                className="px-4 py-[9px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 flex items-center gap-1.5"
              >
                <Video size={13} /> Open the room
              </button>
            </div>
          </div>
        ) : created ? (
          <div className="px-6 py-5 space-y-4">
            <div className="space-y-2.5 max-h-[300px] overflow-y-auto">
              {created.guests.map((g, i) => (
                <div key={g.id || i} className="rounded-[10px] border border-theme bg-tertiary p-3.5">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[12px] font-semibold text-primary-c truncate">{g.name}</span>
                    <button
                      onClick={() => copyLink(i)}
                      className="shrink-0 px-2.5 py-[5px] rounded-lg text-[11px] font-semibold text-secondary-c bg-elevated border border-theme hover:bg-hover-surface transition-all flex items-center gap-1"
                    >
                      {copiedIdx === i ? (<><Check size={12} className="text-emerald-400" /> Copied</>) : (<><Copy size={12} /> Copy link</>)}
                    </button>
                  </div>
                  <div className="text-[11px] text-primary-c font-mono break-all select-all">{g.guest_url}</div>
                  <p className="text-[10px] text-muted-c mt-1">
                    {(g.email || g.phone)
                      ? `Sent to ${[g.email, g.phone].filter(Boolean).join(' and ')}.`
                      : 'No email or phone — this link is their only way in.'}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-c">
              Each link admits one person; don't post them anywhere public.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => navigate(`/telehealth/${created.sessionId}`)}
                className="px-4 py-[9px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 flex items-center gap-1.5"
              >
                <Video size={13} /> Enter the room
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 py-5 space-y-5 overflow-y-auto">
              {error && (
                <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-[10px] px-3.5 py-2.5">{error}</div>
              )}

              <div className="flex gap-2">
                {modeTab('patient', Users, 'Patient')}
                {modeTab('visitor', UserPlus, 'Visitor')}
              </div>

              {(
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-2">When</label>
                  <div className="flex gap-2">
                    {[['now', Zap, 'Right now'], ['later', Clock, 'Schedule for later']].map(([v, Icon, label]) => (
                      <button
                        key={v}
                        onClick={() => setWhen(v)}
                        className={`flex-1 px-3 py-[9px] rounded-[10px] text-[12px] font-semibold border transition-all flex items-center justify-center gap-1.5 ${when === v
                          ? 'bg-accent-glow text-accent-light border-strong'
                          : 'bg-tertiary text-secondary-c border-theme hover:bg-hover-surface'}`}
                      >
                        <Icon size={13} /> {label}
                      </button>
                    ))}
                  </div>
                  {scheduling && (
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_110px_110px] gap-2 mt-2.5">
                      <input
                        type="date"
                        className={inputClass}
                        value={schedDate}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setSchedDate(e.target.value)}
                      />
                      <input
                        type="time"
                        className={inputClass}
                        value={schedTime}
                        onChange={(e) => setSchedTime(e.target.value)}
                      />
                      <select
                        className={inputClass}
                        value={schedDuration}
                        onChange={(e) => setSchedDuration(Number(e.target.value))}
                      >
                        {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {mode === 'patient' ? (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-2">Patient</label>
                  <div className="relative mb-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-c" />
                    <input
                      className={`${inputClass} pl-9`}
                      placeholder="Search patients by name, email, or phone…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="border border-theme rounded-[10px] max-h-[210px] overflow-y-auto divide-y divide-[var(--border-theme,rgba(128,128,128,0.15))]">
                    {contacts === null && (
                      <p className="text-[12px] text-muted-c flex items-center gap-2 px-3.5 py-3">
                        <Loader2 size={13} className="animate-spin" /> Loading patients…
                      </p>
                    )}
                    {contacts !== null && filtered.length === 0 && (
                      <p className="text-[12px] text-muted-c px-3.5 py-3">
                        {search ? 'No patients match that search.' : 'No patients yet — invite a visitor instead, or add one in Contacts.'}
                      </p>
                    )}
                    {filtered.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setSelectedId(c.id)}
                        className={`w-full text-left px-3.5 py-2.5 flex items-center gap-3 transition-all ${selectedId === c.id ? 'bg-accent-glow' : 'hover:bg-hover-surface'}`}
                      >
                        <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${selectedId === c.id ? 'border-[#0DA7CA] bg-[#0DA7CA] text-white' : 'border-strong'}`}>
                          {selectedId === c.id && <Check size={11} strokeWidth={3} />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[13px] font-semibold text-primary-c truncate">
                            {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}
                          </span>
                          <span className="block text-[11px] text-muted-c truncate">
                            {[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact info on file'}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                  {selected && !selected.email && !selected.phone && (
                    <p className="text-[11px] text-amber-400 mt-1.5">
                      This patient has no email or phone on file — you'll need to share the join link with them yourself.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {visitors.map((v, i) => (
                    <div key={i} className="rounded-[10px] border border-theme bg-tertiary p-3.5 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold uppercase tracking-[1px] text-muted-c">
                          Visitor {visitors.length > 1 ? i + 1 : ''} — name *
                        </label>
                        {visitors.length > 1 && (
                          <button
                            onClick={() => removeVisitor(i)}
                            className="w-5 h-5 rounded-md bg-elevated border border-theme text-muted-c flex items-center justify-center hover:text-danger transition-all"
                            title="Remove this visitor"
                          >
                            <X size={11} />
                          </button>
                        )}
                      </div>
                      <input
                        className={inputClass}
                        placeholder="e.g. Maria Alvarez (interpreter)"
                        value={v.name}
                        maxLength={120}
                        onChange={(e) => setVisitor(i, 'name', e.target.value)}
                        autoFocus={i === visitors.length - 1}
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <input
                          className={inputClass}
                          type="email"
                          placeholder="Email"
                          value={v.email}
                          onChange={(e) => setVisitor(i, 'email', e.target.value)}
                        />
                        <input
                          className={inputClass}
                          placeholder="Phone"
                          value={v.phone}
                          onChange={(e) => setVisitor(i, 'phone', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                  {visitors.length < MAX_VISITORS && (
                    <button
                      onClick={addVisitor}
                      className="w-full py-[9px] rounded-[10px] text-[12px] font-semibold text-accent-light border border-dashed border-strong hover:bg-accent-glow transition-all flex items-center justify-center gap-1.5"
                    >
                      <UserPlus size={13} /> Add another visitor
                    </button>
                  )}
                  <p className="text-[11px] text-muted-c">
                    Each visitor gets their own private link — no MediLink account needed. Leave email and phone blank to copy their link yourself.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-2">Reason <span className="font-normal normal-case tracking-normal">(optional)</span></label>
                <input
                  className={inputClass}
                  placeholder="e.g. Follow-up on MRI results"
                  value={title}
                  maxLength={200}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {languages.length > 0 && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[1px] text-muted-c mb-2">Caption &amp; translation languages</label>
                  <div className="flex flex-wrap gap-1.5">
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => toggleLang(l.code)}
                        className={`px-3 py-[6px] rounded-full text-[11px] font-semibold border transition-all ${langs.includes(l.code)
                          ? 'gradient-accent text-white border-transparent'
                          : 'bg-tertiary text-secondary-c border-theme hover:bg-hover-surface'}`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-theme flex items-center justify-between gap-3">
              <button
                onClick={() => navigate('/app/scheduling')}
                className="text-[12px] font-semibold text-accent-light hover:underline flex items-center gap-1.5"
              >
                <CalendarPlus size={13} /> Schedule an appointment instead
              </button>
              <button
                onClick={handleStart}
                disabled={!canStart || starting}
                className="px-4 py-[9px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center gap-1.5"
              >
                {starting
                  ? (<><Loader2 size={13} className="animate-spin" /> {scheduling ? 'Scheduling…' : 'Starting…'}</>)
                  : scheduling
                  ? (<><CalendarPlus size={13} /> Schedule &amp; send invite</>)
                  : (<><Video size={13} /> Start visit &amp; send invite</>)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
